import React, { useState } from 'react';
import { Heart, Activity, Plus, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { THEMES, getColorClasses } from '../pages/themeConfig';

const Medbay = ({ squad, resources, onHealSoldier, theme = 'vanilla' }) => {
  const [healingInProgress, setHealingInProgress] = useState({});
  
  const config = THEMES[theme] || THEMES.vanilla;
  
  // Filter wounded soldiers
  const woundedSoldiers = squad.filter(s => s.health < s.max_health);
  const healthySoldiers = squad.filter(s => s.health === s.max_health);
  
  // Healing cost per soldier
  const HEAL_COST = {
    supplies: 10,
    funds: 500
  };
  
  const HEAL_AMOUNT = 25;
  
  const canAffordHealing = resources.supplies >= HEAL_COST.supplies && 
                           resources.funds >= HEAL_COST.funds;
  
  const handleStartHealing = async (soldier) => {
    if (!canAffordHealing) {
      alert('Insufficient resources for healing!');
      return;
    }
    
    if (soldier.health >= soldier.max_health) {
      alert('Soldier is already at full health!');
      return;
    }
    
    // Show healing animation
    setHealingInProgress(prev => ({ ...prev, [soldier.id]: true }));
    
    // Simulate healing delay
    setTimeout(() => {
      onHealSoldier(soldier);
      setHealingInProgress(prev => {
        const updated = { ...prev };
        delete updated[soldier.id];
        return updated;
      });
    }, 1000);
  };
  
  const getHealthColor = (health, maxHealth) => {
    const percentage = (health / maxHealth) * 100;
    if (percentage >= 80) return 'text-green-400';
    if (percentage >= 50) return 'text-yellow-400';
    if (percentage >= 25) return 'text-orange-400';
    return 'text-red-400';
  };
  
  const getHealthBarColor = (health, maxHealth) => {
    const percentage = (health / maxHealth) * 100;
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Medbay Header */}
      <div className="bg-gray-900/60 backdrop-blur border-2 border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${getColorClasses(config.colors.primary, 'bg')}/20`}>
              <Heart className={getColorClasses(config.colors.primary)} size={32} />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${getColorClasses(config.colors.primary)}`}>
                MEDICAL BAY
              </h2>
              <p className="text-sm text-gray-400">Heal and recover your wounded soldiers</p>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-xs text-gray-400">HEALING COST</p>
            <p className="text-sm font-bold text-yellow-400">{HEAL_COST.supplies} Supplies</p>
            <p className="text-sm font-bold text-green-400">${HEAL_COST.funds}</p>
            <p className="text-xs text-gray-400 mt-1">Restores: {HEAL_AMOUNT} HP</p>
          </div>
        </div>
        
        {/* Resource Check */}
        {!canAffordHealing && (
          <div className="bg-red-900/20 border border-red-500/50 rounded p-3 flex items-center gap-2">
            <AlertCircle className="text-red-400" size={20} />
            <p className="text-sm text-red-400">
              Insufficient resources for healing operations
            </p>
          </div>
        )}
      </div>

      {/* Wounded Soldiers Section */}
      <div className="bg-gray-900/60 backdrop-blur border-2 border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="text-red-400" size={24} />
          <h3 className="text-xl font-bold text-red-400">
            WOUNDED PERSONNEL ({woundedSoldiers.length})
          </h3>
        </div>
        
        {woundedSoldiers.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <CheckCircle className="mx-auto mb-2" size={48} />
            <p className="text-lg">All soldiers are at full health!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {woundedSoldiers.map(soldier => {
              const healthPercent = (soldier.health / soldier.max_health) * 100;
              const isHealing = healingInProgress[soldier.id];
              
              return (
                <div 
                  key={soldier.id}
                  className="bg-gray-800/50 border-2 border-gray-700 hover:border-gray-600 rounded-lg p-4 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-bold text-white text-lg">{soldier.name}</h4>
                      <p className="text-sm text-gray-400">{soldier.rank}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Status: <span className="text-orange-400">{soldier.status}</span>
                      </p>
                    </div>
                    
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${getHealthColor(soldier.health, soldier.max_health)}`}>
                        {soldier.health}
                      </p>
                      <p className="text-xs text-gray-400">/ {soldier.max_health} HP</p>
                    </div>
                  </div>
                  
                  {/* Health Bar */}
                  <div className="mb-3">
                    <div className="bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div 
                        className={`h-full ${getHealthBarColor(soldier.health, soldier.max_health)} transition-all duration-500`}
                        style={{ width: `${healthPercent}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Heal Button */}
                  <button
                    onClick={() => handleStartHealing(soldier)}
                    disabled={!canAffordHealing || isHealing}
                    className={`w-full py-2 rounded font-bold text-sm transition-all ${
                      isHealing
                        ? 'bg-blue-600 text-white cursor-wait'
                        : canAffordHealing
                        ? `${getColorClasses(config.colors.primary, 'bg')} hover:opacity-80 text-white`
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isHealing ? (
                      <span className="flex items-center justify-center gap-2">
                        <Clock className="animate-spin" size={16} />
                        HEALING...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Plus size={16} />
                        HEAL (+{HEAL_AMOUNT} HP)
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Healthy Soldiers Section */}
      <div className="bg-gray-900/60 backdrop-blur border-2 border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle className="text-green-400" size={24} />
          <h3 className="text-xl font-bold text-green-400">
            COMBAT READY ({healthySoldiers.length})
          </h3>
        </div>
        
        {healthySoldiers.length === 0 ? (
          <div className="text-center py-4 text-gray-400">
            <p>No soldiers at full health</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {healthySoldiers.map(soldier => (
              <div 
                key={soldier.id}
                className="bg-gray-800/50 border border-green-500/30 rounded-lg p-3"
              >
                <h4 className="font-bold text-white text-sm">{soldier.name}</h4>
                <p className="text-xs text-gray-400">{soldier.rank}</p>
                <div className="mt-2 flex items-center gap-1">
                  <Heart className="text-green-400" size={14} />
                  <p className="text-xs font-bold text-green-400">
                    {soldier.health}/{soldier.max_health}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Panel */}
      <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-400 flex-shrink-0 mt-1" size={20} />
          <div className="text-sm text-blue-200">
            <p className="font-bold mb-1">MEDICAL BAY OPERATIONS</p>
            <ul className="space-y-1 text-xs">
              <li>• Each healing session costs {HEAL_COST.supplies} Supplies and ${HEAL_COST.funds}</li>
              <li>• Soldiers recover {HEAL_AMOUNT} HP per treatment</li>
              <li>• Multiple treatments may be needed for severely wounded personnel</li>
              <li>• Wounded soldiers cannot be deployed on missions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Medbay;